import { useEffect, useState, type FormEvent } from 'react'

import { FormulaRefusalError, getShowcaseFormula, putShowcaseFormula } from '@/lib/api/client/ymarket-pricing'
import type { FormulaDefinition } from '@/lib/api/schemas/ymarket-pricing.schema'

export const COST_PER_GRAM_KEY = 'costPerGram'

function sourceOf(definition: FormulaDefinition | null): string {
  return definition === null ? '' : definition.source.trim()
}

function message(reason: unknown, fallback: string): string {
  if (reason instanceof FormulaRefusalError) return reason.position === null ? reason.message : `Символ ${reason.position}: ${reason.message}`
  return reason instanceof Error && reason.message.length > 0 ? reason.message : fallback
}

/** The owner's ₽/г formula that prices a sold piece whose purchase cost 1C does not hold. */
export function CostEstimateSettings() {
  const [definition, setDefinition] = useState<FormulaDefinition | null>(null)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  const adopt = (formulas: FormulaDefinition[]) => {
    const found = formulas.find((item) => item.key === COST_PER_GRAM_KEY) ?? null
    setDefinition(found)
    setDraft(sourceOf(found))
  }

  useEffect(() => {
    const controller = new AbortController()
    getShowcaseFormula(controller.signal)
      .then((value) => { adopt(value.formulas); setError(null) })
      .catch((reason: unknown) => { if (!controller.signal.aborted) setError(message(reason, 'Не удалось загрузить оценку себестоимости')) })
    return () => controller.abort()
  }, [])

  const source = draft.trim()
  const changed = definition !== null && source.length > 0 && source !== sourceOf(definition)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!changed) return
    setBusy(true)
    setSaved(false)
    try {
      adopt((await putShowcaseFormula({ formulas: { [COST_PER_GRAM_KEY]: source }, note: 'оценка себестоимости, ₽/г' })).formulas)
      setError(null)
      setSaved(true)
    } catch (reason: unknown) {
      setError(message(reason, 'Не удалось сохранить оценку себестоимости'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="mt-4 rounded-lg border border-rule bg-paper p-4 sm:p-5" aria-label="Себестоимость по оценке">
      <h2 className="text-[15px] font-semibold text-ink">Себестоимость по оценке</h2>
      <p className="mt-1 text-xs text-ink-muted">
        Изделию, у которого в 1С нет закупочной цены, юнит-экономика ставит оценку: результат формулы, ₽ за грамм, × вес по 1С, с пометкой «оценка». По умолчанию изделие из переноса остатков 2020 года оценивается как розница 2020 ÷ 2 (transferRetail), остальные — по ставке металла: серебро 690 ₽/г, золото 6 000 ₽/г. Изделию без металла ставки металла нет — оно остаётся без себестоимости с причиной. Себестоимость из 1С всегда важнее; без веса оценки нет. Новая формула действует со следующей загрузки страницы.
      </p>
      {error !== null ? <p role="alert" className="mt-3 text-xs text-st-unpaid">{error}</p> : null}
      {saved ? <p aria-live="polite" className="mt-3 text-xs text-ink-2">Формула сохранена.</p> : null}
      {definition === null ? (
        error === null ? <p aria-live="polite" className="mt-3 text-xs text-ink-muted">Загружаем формулу…</p> : null
      ) : (
        <form className="mt-3 space-y-2" onSubmit={(event) => { void submit(event) }}>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Формула, ₽ за грамм
            <textarea
              className="w-full rounded-md border border-rule bg-paper px-2.5 py-1.5 font-mono text-sm"
              rows={2}
              spellCheck={false}
              value={draft}
              onChange={(event) => { setDraft(event.target.value); setSaved(false) }}
            />
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button type="submit" className="btn btn-primary" disabled={busy || !changed}>Сохранить</button>
            {source !== definition.default ? (
              <button type="button" className="text-xs underline" onClick={() => { setDraft(definition.default); setSaved(false) }}>Подставить формулу по умолчанию</button>
            ) : null}
            {definition.builtIn ? <span className="text-xs text-ink-faint">действует формула по умолчанию</span> : null}
          </div>
          {definition.storedError !== null ? (
            <p className="text-xs text-st-unpaid">Сохранённая формула не проходит проверку ({definition.storedError}); оценки нет, пока её не исправить.</p>
          ) : null}
          <ul className="space-y-0.5 text-xs text-ink-muted" aria-label="Переменные оценки">
            {definition.variables.map((variable) => (
              <li key={variable.name}>
                <span className="font-mono text-ink">{variable.name}</span> — {variable.label}. {variable.description}
                {variable.values === null ? '' : ` Значения: ${variable.values.join(', ')}.`}
              </li>
            ))}
          </ul>
        </form>
      )}
    </section>
  )
}
